import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Download,
  Upload,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  LayoutList,
  LayoutGrid,
  Table,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { useCashFlow } from '@hooks/useCashFlow';
import { useTransactions } from '@hooks/useTransactions';
import { useInstallments } from '@hooks/useInstallments';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import PageTutorial from '@components/common/PageTutorial';
import TransactionFilters from '@components/transactions/TransactionFilters';
import DailyBalanceTimeline from '@components/transactions/DailyBalanceTimeline';
import UnifiedTransactionList from '@components/transactions/UnifiedTransactionList';
import TransactionTable from '@components/transactions/TransactionTable';
import TransactionTimeline from '@components/transactions/TransactionTimeline';
import TransactionForm from '@components/transactions/TransactionForm';
import ImportTransactions from '@components/transactions/ImportTransactions';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// ─── MetricCard ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, icon, iconBg, valueColor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const Transactions = () => {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedMonth, setSelectedMonth] = useState({
    year: now.getFullYear(),
    month: now.getMonth(),
  });

  const [filters, setFilters] = useState(() => {
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      search: '',
      type: '',
      status: '',
      accountId: '',
      categoryId: '',
    };
  });

  const [periodMode, setPeriodMode] = useState('month');

  // Ler ?q= da URL (vindo da busca do header)
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setFilters(prev => ({ ...prev, search: q }));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Atalho global N → abrir modal de nova transação
  useEffect(() => {
    const handler = () => setIsModalOpen(true);
    window.addEventListener('shortcut:newTransaction', handler);
    return () => window.removeEventListener('shortcut:newTransaction', handler);
  }, []);

  // Estado do calendário modal (navegação independente do filtro principal)
  const [calendarMonth, setCalendarMonth] = useState({
    year: now.getFullYear(),
    month: now.getMonth(),
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');

  // Para meses futuros, busca desde o início do mês atual para acumular saldo corretamente
  const nowMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const isFutureMonth = filters.startDate > nowMonthStart;
  const effectiveStartDate = isFutureMonth ? nowMonthStart : filters.startDate;

  // Dados principais (filtro da página)
  const {
    items: allItems,
    dailyBalances: allDailyBalances,
    isLoading,
    refetch,
  } = useCashFlow({
    startDate: effectiveStartDate,
    endDate: filters.endDate,
    accountId: filters.accountId || undefined,
    categoryId: filters.categoryId || undefined,
    type: filters.type || undefined,
  });

  // Filtra para exibir apenas o mês selecionado (saldo já está acumulado)
  const items = useMemo(
    () => allItems.filter(i => i.date >= filters.startDate && i.date <= filters.endDate),
    [allItems, filters.startDate, filters.endDate]
  );

  const dailyBalances = useMemo(
    () => allDailyBalances.filter(d => d.date >= filters.startDate && d.date <= filters.endDate),
    [allDailyBalances, filters.startDate, filters.endDate]
  );

  // Recalcula o summary apenas do mês selecionado
  const summary = useMemo(() => {
    return {
      totalIncome: items.filter(i => i.type === 'income').reduce((s, i) => s + Math.abs(i.amount), 0),
      totalExpense: items.filter(i => i.type === 'expense').reduce((s, i) => s + Math.abs(i.amount), 0),
      pendingIncome: items.filter(i => i.type === 'income' && i.status === 'pending').reduce((s, i) => s + Math.abs(i.amount), 0),
      pendingExpense: items.filter(i => i.type === 'expense' && i.status === 'pending').reduce((s, i) => s + Math.abs(i.amount), 0),
      completedIncome: items.filter(i => i.type === 'income' && i.status === 'completed').reduce((s, i) => s + Math.abs(i.amount), 0),
      completedExpense: items.filter(i => i.type === 'expense' && i.status === 'completed').reduce((s, i) => s + Math.abs(i.amount), 0),
    };
  }, [items]);

  // Dados do calendário modal
  const calendarStartDate = useMemo(() => {
    const nowMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const calMonthStart = new Date(calendarMonth.year, calendarMonth.month, 1);
    const start = calMonthStart > nowMonthStart ? nowMonthStart : calMonthStart;
    return start.toISOString().split('T')[0];
  }, [calendarMonth]);

  const calendarEndDate = useMemo(() => {
    return new Date(calendarMonth.year, calendarMonth.month + 1, 0)
      .toISOString().split('T')[0];
  }, [calendarMonth]);

  const { dailyBalances: allCalendarBalances, isLoading: isCalendarLoading } = useCashFlow({
    startDate: calendarStartDate,
    endDate: calendarEndDate,
  });

  const calendarDailyBalances = useMemo(() => {
    return allCalendarBalances.filter(d => {
      const date = new Date(d.date + 'T12:00:00');
      return date.getFullYear() === calendarMonth.year && date.getMonth() === calendarMonth.month;
    });
  }, [allCalendarBalances, calendarMonth]);

  const {
    createTransaction,
    updateTransaction,
    deleteTransaction,
    isCreating,
    isUpdating,
    isDeleting,
  } = useTransactions();

  const { payInstallment } = useInstallments();
  const { accounts } = useAccounts();
  const { categories } = useCategories();

  const filteredItems = useMemo(() => {
    let result = [...items];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(item =>
        item.description.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status) {
      result = result.filter(item => item.status === filters.status);
    }

    if (selectedDate) {
      result = result.filter(item => item.date === selectedDate);
    }

    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'date') {
        comparison = a.date.localeCompare(b.date);
      } else if (sortField === 'amount') {
        comparison = Math.abs(a.amount) - Math.abs(b.amount);
      } else if (sortField === 'description') {
        comparison = a.description.localeCompare(b.description);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [items, filters.search, filters.status, selectedDate, sortField, sortDirection]);

  const handleMonthChange = (newMonth) => {
    setPeriodMode('month');
    setSelectedMonth(newMonth);
    const startDate = new Date(newMonth.year, newMonth.month, 1);
    const endDate = new Date(newMonth.year, newMonth.month + 1, 0);
    setFilters(prev => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    }));
    setSelectedDate(null);
  };

  const handlePeriodMode = (mode) => {
    setPeriodMode(mode);
    if (mode === 'custom') return; // usuário define as datas nos campos
    if (mode === 'month') {
      handleMonthChange(selectedMonth);
      return;
    }
    const monthsBack = mode === '3m' ? 2 : mode === '6m' ? 5 : 11; // 12m
    const startDate = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setFilters((prev) => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    }));
    setSelectedDate(null);
  };

  const handleClearFilters = () => {
    setPeriodMode('month');
    const n = new Date();
    const newMonth = { year: n.getFullYear(), month: n.getMonth() };
    setSelectedMonth(newMonth);
    const startDate = new Date(n.getFullYear(), n.getMonth(), 1);
    const endDate = new Date(n.getFullYear(), n.getMonth() + 1, 0);
    setFilters({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      search: '',
      type: '',
      status: '',
      accountId: '',
      categoryId: '',
    });
    setSelectedDate(null);
  };

  const handleOpenCalendar = () => {
    setCalendarMonth(selectedMonth);
    setIsCalendarOpen(true);
  };

  const handleCalendarPrevMonth = () => {
    setCalendarMonth(prev =>
      prev.month === 0
        ? { year: prev.year - 1, month: 11 }
        : { ...prev, month: prev.month - 1 }
    );
  };

  const handleCalendarNextMonth = () => {
    setCalendarMonth(prev =>
      prev.month === 11
        ? { year: prev.year + 1, month: 0 }
        : { ...prev, month: prev.month + 1 }
    );
  };

  const isFutureCalendarMonth = (() => {
    const nowMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const calMonth = new Date(calendarMonth.year, calendarMonth.month, 1);
    return calMonth > nowMonth;
  })();

  const handleExport = () => {
    const headers = ['Data', 'Descrição', 'Tipo', 'Valor', 'Categoria', 'Conta', 'Status'];
    const rows = filteredItems.map(item => {
      const category = categories.find(c => c.categoryId === item.categoryId);
      const account = accounts.find(a => a.accountId === item.accountId);
      return [
        item.date,
        `"${item.description.replace(/"/g, '""')}"`,
        item.type === 'income' ? 'Receita' : 'Despesa',
        Math.abs(item.amount).toFixed(2).replace('.', ','),
        category?.name || '',
        account?.name || '',
        item.status === 'completed' ? 'Realizada' : 'Prevista',
      ];
    });
    const csv = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transacoes-${filters.startDate}-${filters.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenModal = (transaction = null) => {
    if (transaction?.source === 'transaction') {
      setSelectedTransaction(transaction);
    } else {
      setSelectedTransaction(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTransaction(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (data) => {
    if (selectedTransaction && selectedTransaction.transactionId) {
      await updateTransaction({
        transactionId: selectedTransaction.transactionId,
        date: selectedTransaction.date,
        data,
      });
    } else {
      await createTransaction(data);
    }
    handleCloseModal();
    await refetch();
  };

  const handleDelete = async () => {
    if (selectedTransaction) {
      await deleteTransaction({
        transactionId: selectedTransaction.transactionId,
        date: selectedTransaction.date,
      });
      setIsDeleteModalOpen(false);
      setSelectedTransaction(null);
      await refetch();
    }
  };

  const handleDuplicate = (item) => {
    setSelectedTransaction({
      prefill: {
        description: item.description,
        amount: Math.abs(item.amount),
        type: item.type,
        date: new Date().toISOString().split('T')[0],
        accountId: item.accountId,
        categoryId: item.categoryId,
      }
    });
    setIsModalOpen(true);
  };

  const handlePayInstallment = async (item) => {
    await payInstallment({
      installmentId: item.installmentId,
      installmentNumber: item.installmentNumber,
      paymentDate: new Date().toISOString().split('T')[0],
    });
    await refetch();
  };

  const handleLaunchRecurrence = async (item) => {
    await createTransaction({
      description: item.description,
      amount: Math.abs(item.amount),
      type: item.type,
      date: item.date,
      accountId: item.accountId,
      categoryId: item.categoryId,
      isRecurring: true,
      recurringId: item.recurrenceId,
    });
    await refetch();
  };

  const handleDayClick = (date) => {
    setSelectedDate(selectedDate === date ? null : date);
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  if (isLoading) {
    return <Loading text="Carregando transações..." />;
  }

  const monthBalance = (summary?.completedIncome || 0) - (summary?.completedExpense || 0);
  const pendingBalance = (summary?.pendingIncome || 0) - (summary?.pendingExpense || 0);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Transações"
        subtitle="Visão completa do seu fluxo de caixa"
        action={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<CalendarDays size={18} />}
              onClick={handleOpenCalendar}
            >
              Calendario
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Upload size={18} />}
              onClick={() => setIsImportModalOpen(true)}
            >
              Importar OFX
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Download size={18} />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            <Button leftIcon={<Plus size={18} />} onClick={() => handleOpenModal()}>
              Nova Transacao
            </Button>
          </div>
        }
      />

      <PageTutorial
        pageId="transactions"
        title="Gerenciando transacoes"
        steps={[
          'Use os filtros para encontrar transacoes por tipo, conta, categoria ou busca',
          'Alterne entre Tabela, Cards e Timeline para diferentes visualizacoes',
          'O calendario de saldo mostra quanto voce tinha a cada dia do mes',
          'Importe extratos bancarios no formato OFX para cadastrar transacoes em lote',
          'Transacoes previstas (amarelas) sao futuras; realizadas (verdes) ja aconteceram',
        ]}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Receitas"
          value={formatCurrency(summary?.totalIncome || 0)}
          subtitle={`${formatCurrency(summary?.completedIncome || 0)} realizado + ${formatCurrency(summary?.pendingIncome || 0)} previsto`}
          icon={<TrendingUp size={18} className="text-emerald-600" />}
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
          valueColor="text-emerald-600"
        />
        <MetricCard
          title="Despesas"
          value={formatCurrency(summary?.totalExpense || 0)}
          subtitle={`${formatCurrency(summary?.completedExpense || 0)} realizado + ${formatCurrency(summary?.pendingExpense || 0)} previsto`}
          icon={<TrendingDown size={18} className="text-red-600" />}
          iconBg="bg-red-100 dark:bg-red-900/30"
          valueColor="text-red-600"
        />
        <MetricCard
          title="Saldo Realizado"
          value={formatCurrency(monthBalance)}
          subtitle="receitas − despesas confirmadas"
          icon={
            monthBalance >= 0
              ? <CheckCircle size={18} className="text-primary-600" />
              : <CheckCircle size={18} className="text-red-600" />
          }
          iconBg={monthBalance >= 0 ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-red-100 dark:bg-red-900/30'}
          valueColor={monthBalance >= 0 ? 'text-primary-600' : 'text-red-600'}
        />
        <MetricCard
          title="Saldo Previsto"
          value={formatCurrency(pendingBalance)}
          subtitle="transações ainda não confirmadas"
          icon={
            pendingBalance >= 0
              ? <Clock size={18} className="text-yellow-600" />
              : <Clock size={18} className="text-red-600" />
          }
          iconBg={pendingBalance >= 0 ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-red-100 dark:bg-red-900/30'}
          valueColor={pendingBalance >= 0 ? 'text-yellow-600' : 'text-red-600'}
        />
      </div>

      {/* Aviso de saldo projetado para meses futuros */}
      {isFutureMonth && (
        <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl flex items-center gap-3">
          <Clock size={18} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            <strong>Saldo projetado acumulado</strong> — os valores consideram todas as receitas e despesas previstas desde{' '}
            {MONTHS[now.getMonth()]} até {MONTHS[selectedMonth.month]}.
          </p>
        </div>
      )}

      {/* Filtros */}
      <div>
        <TransactionFilters
          filters={filters}
          onChange={setFilters}
          onClear={handleClearFilters}
          selectedMonth={selectedMonth}
          onMonthChange={handleMonthChange}
          periodMode={periodMode}
          onPeriodMode={handlePeriodMode}
        />
      </div>

      {/* Toggle de Visualização */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1">
          {[
            { mode: 'table', icon: <Table size={16} />, label: 'Tabela' },
            { mode: 'cards', icon: <LayoutGrid size={16} />, label: 'Cards' },
            { mode: 'timeline', icon: <LayoutList size={16} />, label: 'Timeline' },
          ].map(({ mode, icon, label }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                viewMode === mode
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              {icon}
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
          {filteredItems.length} transação(ões) encontrada(s)
          {selectedDate && (
            <span className="ml-2">
              • Dia selecionado: {new Date(selectedDate + 'T00:00:00').toLocaleDateString('pt-BR')}
              <button
                onClick={() => setSelectedDate(null)}
                className="ml-2 text-primary-600 hover:underline"
              >
                Limpar
              </button>
            </span>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      {viewMode === 'table' && (
        <TransactionTable
          items={filteredItems}
          dailyBalances={dailyBalances}
          onEdit={handleOpenModal}
          onDelete={(item) => {
            setSelectedTransaction(item);
            setIsDeleteModalOpen(true);
          }}
          onDuplicate={handleDuplicate}
          onPayInstallment={handlePayInstallment}
          onLaunchRecurrence={handleLaunchRecurrence}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      {viewMode === 'cards' && (
        <UnifiedTransactionList
          items={filteredItems}
          onEdit={handleOpenModal}
          onDelete={(item) => {
            setSelectedTransaction(item);
            setIsDeleteModalOpen(true);
          }}
          onPayInstallment={handlePayInstallment}
          onLaunchRecurrence={handleLaunchRecurrence}
          selectedDate={selectedDate}
        />
      )}

      {viewMode === 'timeline' && (
        <TransactionTimeline
          items={filteredItems}
          onEdit={handleOpenModal}
          onDelete={(item) => {
            setSelectedTransaction(item);
            setIsDeleteModalOpen(true);
          }}
          onPayInstallment={handlePayInstallment}
          onLaunchRecurrence={handleLaunchRecurrence}
          selectedDate={selectedDate}
        />
      )}

      {/* Modal Calendário de Saldo Diário */}
      <Modal
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        title="Calendário de Saldo"
        size="lg"
      >
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCalendarPrevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {MONTHS[calendarMonth.month]} {calendarMonth.year}
            </p>
            {isFutureCalendarMonth && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                Saldo projetado acumulado desde {MONTHS[now.getMonth()]}
              </p>
            )}
          </div>

          <button
            onClick={handleCalendarNextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {isCalendarLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <DailyBalanceTimeline
            dailyBalances={calendarDailyBalances}
            onDayClick={(date) => {
              handleDayClick(date);
              if (
                calendarMonth.year !== selectedMonth.year ||
                calendarMonth.month !== selectedMonth.month
              ) {
                handleMonthChange(calendarMonth);
              }
              setIsCalendarOpen(false);
            }}
            selectedDate={selectedDate}
          />
        )}
      </Modal>

      {/* Modal de Transação */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedTransaction?.transactionId ? 'Editar Transação' : 'Nova Transação'}
        size="md"
      >
        <TransactionForm
          transaction={selectedTransaction?.prefill ? { ...selectedTransaction.prefill } : selectedTransaction}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={isCreating || isUpdating}
        />
      </Modal>

      {/* Modal de Confirmacao de Exclusao */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Transacao"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir a transacao{' '}
          <strong>{selectedTransaction?.description}</strong>? Esta acao nao pode ser desfeita.
        </p>
      </Modal>

      {/* Modal de Importacao OFX */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title="Importar Transacoes (OFX)"
        size="lg"
      >
        <ImportTransactions
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={() => refetch()}
        />
      </Modal>
    </div>
  );
};

export default Transactions;
